import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { formatCurrency, formatDate } from '../../lib/utils';
import EntrepriseSidebar from '../Layout/EntrepriseSidebar';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Import des composants des onglets
import EmployesTab from '../Enterprise/EmployesTab';
import HistoriquePaiementsTab from '../Enterprise/HistoriquePaiementsTab';
import RechargeTab from '../Enterprise/RechargeTab';
import RapportsTab from '../Enterprise/RapportsTab';
import ParametresTab from '../Enterprise/ParametresTab';
import EntrepriseAnalyticsTab from '../Enterprise/EntrepriseAnalyticsTab';
import PrimesTab from './PrimesTab';

const EntrepriseLayout = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({
    nb_employes: 0,
    nb_demandes_mois: 0,
    montant_avances_mois: 0,
    solde: 0
  });
  const [employes, setEmployes] = useState([]);
  const [demandes, setDemandes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchEntrepriseData();
  }, []);

  const fetchEntrepriseData = async () => {
    try {
      setIsLoading(true);
      
      // Importer api depuis lib/api.js
      const api = (await import('../../lib/api')).default;
      
      // Charger vraies données depuis API
      const [dashboardRes, employesRes, demandesRes] = await Promise.all([
        api.get('/entreprise/dashboard'),
        api.get('/entreprise/employes'),
        api.get('/entreprise/demandes')
      ]);
      
      setStats(dashboardRes.data);
      setEmployes(employesRes.data.employes || []);
      setDemandes(demandesRes.data.demandes || []);
      
    } catch (error) {
      console.error('Erreur chargement données entreprise:', error);
      setError(error.response?.data?.detail || 'Erreur lors du chargement des données');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des données entreprise...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen bg-gray-50">
        <div className="flex-1 flex items-center justify-center">
          <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg max-w-md">
            <h3 className="font-bold mb-2">Erreur de chargement</h3>
            <p>{error}</p>
            <Button onClick={fetchEntrepriseData} className="mt-4">
              Réessayer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <EntrepriseSidebar 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        onLogout={logout}
        stats={stats}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      
      {/* Contenu principal */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* En-tête */}
        <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex justify-between items-center gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 -ml-2 rounded-md text-gray-600 hover:bg-gray-100 flex-shrink-0"
                data-testid="entreprise-menu-button"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                  {getTabTitle(activeTab)}
                </h1>
                <p className="text-gray-600 text-xs sm:text-sm truncate hidden sm:block">
                  {getTabDescription(activeTab)}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
              <div className="text-right hidden md:block">
                <p className="text-sm text-gray-600">Connecté en tant que</p>
                <p className="font-medium text-gray-900">{user?.role}</p>
              </div>
              <Button 
                onClick={() => {
                  if (window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
                    logout();
                  }
                }}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white font-medium"
                title="Se déconnecter"
              >
                <span className="hidden sm:inline">🚪 Déconnexion</span>
                <span className="sm:hidden">🚪</span>
              </Button>
            </div>
          </div>
        </header>

        {/* Contenu */}
        <main className="flex-1 overflow-auto p-4 sm:p-6">
          {activeTab === 'dashboard' && <DashboardTab stats={stats} demandes={demandes} setActiveTab={setActiveTab} />}
          {activeTab === 'employes' && <EmployesTab employes={employes} fetchData={fetchEntrepriseData} />}
          {activeTab === 'analytics' && <EntrepriseAnalyticsTab />}
          {activeTab === 'demandes' && <HistoriquePaiementsTab />}
          {activeTab === 'solde' && <RechargeTab />}
          {activeTab === 'primes' && <PrimesTab />}
          {activeTab === 'rapports' && <RapportsTab />}
          {activeTab === 'parametres' && <ParametresTab />}
        </main>
      </div>
      
      {/* Toast notifications */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

// Fonctions utilitaires
const getTabTitle = (tab) => {
  const titles = {
    dashboard: 'Tableau de bord',
    employes: 'Gestion des employés',
    demandes: 'Demandes d\'avances',
    solde: 'Gestion du solde',
    primes: 'Gestion des Primes',
    rapports: 'Rapports & Exports',
    parametres: 'Paramètres',
    analytics: 'Analytics'
  };
  return titles[tab] || 'TECH SOLUTIONS SARL';
};

const getTabDescription = (tab) => {
  const descriptions = {
    dashboard: 'Vue d\'ensemble de l\'activité de votre entreprise',
    employes: 'Gérez vos employés et leur éligibilité aux avances',
    demandes: 'Validez et suivez les demandes d\'avances',
    solde: 'Gérez votre solde et effectuez des recharges',
    primes: 'Créez et distribuez des primes à vos employés',
    rapports: 'Générez des rapports et exports pour la paie',
    parametres: 'Configurez les paramètres de votre entreprise',
    analytics: 'Statistiques et analyses'
  };
  return descriptions[tab] || '';
};

// Import StatCard
import StatCard from '../ui/StatCard';

// Composant Dashboard
const DashboardTab = ({ stats, demandes, setActiveTab }) => {
  return (
    <div className="space-y-6">
      {/* Message de bienvenue */}
      <div className="bg-gradient-to-r from-violet-500 via-purple-500 to-pink-500 rounded-lg p-6 text-white shadow-lg">
        <h2 className="text-2xl font-bold mb-2">👋 Bienvenue sur ALO ADVANCE</h2>
        <p className="text-white/90">Vue d'ensemble de votre activité et de vos finances en temps réel</p>
      </div>

      {/* KPIs avec StatCard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="👥 Employés Actifs"
          value={stats.nb_employes}
          icon="👥"
          gradient="from-blue-500 to-blue-600"
          subtitle="Total actifs"
        />

        <StatCard
          title="📋 Demandes ce Mois"
          value={stats.nb_demandes_mois}
          icon="📋"
          gradient="from-green-500 to-green-600"
          subtitle="Approuvées"
        />

        <StatCard
          title="💸 Montant Avancé"
          value={stats.montant_avances_mois}
          icon="💸"
          gradient="from-orange-500 to-orange-600"
          subtitle="Ce mois"
        />

        <StatCard
          title="💰 Solde Disponible"
          value={stats.solde}
          icon="💰"
          gradient="from-purple-500 to-purple-600"
          subtitle="Compte entreprise"
        />
      </div>

      {/* Actions rapides */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card 
          className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200 cursor-pointer hover:shadow-lg transition-all transform hover:-translate-y-1"
          onClick={() => setActiveTab('analytics')}
        >
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="text-4xl">📊</div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Analytics</h3>
                <p className="text-sm text-gray-600">Voir les statistiques détaillées</p>
              </div>
              <div className="text-violet-600">→</div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200 cursor-pointer hover:shadow-lg transition-all transform hover:-translate-y-1"
          onClick={() => setActiveTab('employes')}
        >
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="text-4xl">👥</div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Employés</h3>
                <p className="text-sm text-gray-600">Gérer vos employés</p>
              </div>
              <div className="text-blue-600">→</div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 cursor-pointer hover:shadow-lg transition-all transform hover:-translate-y-1"
          onClick={() => setActiveTab('solde')}
        >
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="text-4xl">💰</div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Recharger</h3>
                <p className="text-sm text-gray-600">Gérer votre solde</p>
              </div>
              <div className="text-green-600">→</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Demandes récentes */}
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <span className="mr-2">📋</span>
              Demandes récentes
            </span>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => setActiveTab('demandes')}
            >
              Voir toutes
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {demandes.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4 opacity-50">📋</div>
              <p className="text-gray-500 font-medium">Aucune demande récente</p>
              <p className="text-sm text-gray-400 mt-2">Les demandes d'avances apparaîtront ici</p>
            </div>
          ) : (
            <div className="space-y-3">
              {demandes.slice(0, 5).map((demande) => (
                <div 
                  key={demande.id} 
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-100 hover:shadow-md transition-all"
                >
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center text-violet-600 font-bold">
                      {demande.employe_nom?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{demande.employe_nom}</p>
                      <p className="text-sm text-gray-600">
                        {formatCurrency(demande.montant_brut)} • {formatDate(demande.date_demande)}
                      </p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    demande.etat === 'SOUMISE' 
                      ? 'bg-yellow-100 text-yellow-800'
                      : demande.etat === 'VALIDEE_RH'
                      ? 'bg-blue-100 text-blue-800'
                      : demande.etat === 'EN_DECAISSEMENT'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {demande.etat.replace(/_/g, ' ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EntrepriseLayout;